import { getHazardList } from "@/features/branding";
import { decryptData, getSecretKey } from "@/features/cryptography";
import { type LargeSiteView, type PortfolioView } from "@/store/services/supabase";
import { type AutoAdaptationStatus, type PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { type KeyPairAndSalt } from "@/types/cryptography";
import { hexToArrayBuffer } from "@/utils/crypto";

interface CollectionKeys {
  user?: KeyPairAndSalt | null;
  org?: KeyPairAndSalt | null;
}

/** Whether the keys needed to decrypt this collection are unlocked. */
export const hasCollectionKey = (row: PortfolioView | LargeSiteView, keys: CollectionKeys): boolean =>
  row.is_private ? !!keys.user : !!keys.org;

/**
 * Decrypts a `v_portfolios` / `v_large_sites` row into the schema the collection page and report data loaders use.
 * Throws if the key it was encrypted against isn't unlocked.
 */
export const toCollectionSchema = async (
  row: PortfolioView | LargeSiteView,
  isLargeSite: boolean,
  keys: CollectionKeys,
  untitledName: string
): Promise<PortfolioExportJobSchema> => {
  const privateKey = row.is_private ? keys.user?.keyPair.privateKey : keys.org?.keyPair.privateKey;

  if (!privateKey) {
    throw new Error(`Collection ${row.id} requires ${row.is_private ? "user" : "org"} private key`);
  }

  const secret = await getSecretKey(privateKey, row.secret_enc!);
  const name = row.name ? await decryptData(secret, hexToArrayBuffer(row.name)) : untitledName;
  const metadata = row.metadata ? JSON.parse(await decryptData(secret, hexToArrayBuffer(row.metadata))) : undefined;

  return {
    id: row.id!,
    name,
    assets: {
      errorCount: row.error || 0,
      processedCount: row.success || 0,
      totalCount: row.total || 0,
      unprocessedCount: row.unprocessed || 0,
    },
    startTime: new Date(row.created_at!).getTime(),
    summaryId: row.summary_id || "",
    userId: row.user_id!,
    email: row.email!,
    rerunEmail: row.rerun_email ?? null,
    rerunAt: row.rerun_at ?? null,
    groupId: row.group_id || "",
    groupName: row.group_name || "",
    secret,
    isLargeSite,
    isPrivate: row.is_private || false,
    stamps: row.stamps || [],
    metadata,
    incorporatedHazards: ("incorporated_hazards" in row && row.incorporated_hazards
      ? row.incorporated_hazards
      : getHazardList().fallback) as any[],
    status: "completed",
    isSummaryGenerated: row.is_summary_generated,
    // Only portfolios carry one; `v_large_sites` has no such column.
    regionType: "region_type" in row && row.region_type ? (row.region_type as "hk" | "global") : undefined,
    autoAdaptationStatus: (row.auto_adaptation_status ?? null) as AutoAdaptationStatus,
    collectionVersion: "collection_version" in row ? (row.collection_version ?? null) : null,
    updateAvailable: row.update_available ?? false,
    adaptations: undefined,
  };
};
